import Skeleton from 'react-loading-skeleton'
import { useMobile } from 'src/hooks/useMobile'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import SliderControls from './SliderControls'
import './slider.scss'
import { NextButton, PrevButton } from './SliderButton'

type SliderProps = {
  title?: string
  itemQuantity: number
  skeletonStyles?: string
  hasScrollbar?: boolean
  children?: React.ReactNode
  controlsStyle?: 'top' | 'middle'
  customStepWidth?: {
    desktop?: number
    tablet?: number
    mobile?: number
  }
  customStepQtty?: {
    desktop?: number
    tablet?: number
    mobile?: number
  }
  renderOnlyActiveControls?: boolean
  disableInactiveControls?: boolean
  onNavigationStateChange?: (state: { prev: boolean; next: boolean }) => void
}

const Slider = ({
  title,
  itemQuantity,
  skeletonStyles,
  hasScrollbar = true,
  children,
  controlsStyle = 'top',
  customStepWidth,
  customStepQtty,
  renderOnlyActiveControls = false,
  disableInactiveControls = false,
  onNavigationStateChange,
}: SliderProps) => {
  const { isMobile, screenWidth } = useMobile()
  const listRef = useRef<HTMLUListElement | null>(null)
  const thumbRef = useRef<HTMLDivElement>(null)
  const isDraggingRef = useRef(false)
  const initialScrollLeftRef = useRef<number>(0)
  const startXRef = useRef<number>(0)
  const [navState, setNavState] = useState<{
    prev: boolean
    next: boolean
  }>({ prev: false, next: true })

  const [thumbWidth, setThumbWidth] = useState(20)
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [isDragging, setIsDragging] = useState(false)
  const SPEED_MULTIPLIER = -3
  const NAVIGATION_TOLERANCE_PX = 1

  const step = useMemo(() => {
    if (screenWidth > 1024) {
      return {
        width: customStepWidth?.desktop ?? 324,
        qtty: customStepQtty?.desktop ?? 4,
      }
    }

    if (screenWidth > 768) {
      return {
        width: customStepWidth?.tablet ?? 266,
        qtty: customStepQtty?.tablet ?? 3,
      }
    }

    return {
      width: customStepWidth?.mobile ?? 166,
      qtty: customStepQtty?.mobile ?? 2,
    }
  }, [screenWidth, customStepWidth, customStepQtty])

  const updateNavigationState = useCallback(() => {
    if (!listRef.current) {
      return
    }

    const { scrollWidth, scrollLeft, clientWidth } = listRef.current
    const maxScrollLeft = Math.max(scrollWidth - clientWidth, 0)
    const remainingScroll = Math.max(maxScrollLeft - scrollLeft, 0)
    const nextNavState = {
      prev: scrollLeft > NAVIGATION_TOLERANCE_PX,
      next: remainingScroll > NAVIGATION_TOLERANCE_PX,
    }

    onNavigationStateChange?.(nextNavState)

    const nextPage = Math.round(scrollLeft / step.width)

    setNavState((prevState) => {
      if (
        prevState.prev === nextNavState.prev &&
        prevState.next === nextNavState.next
      ) {
        return prevState
      }

      return nextNavState
    })

    setCurrentPage((prevPage) => {
      if (prevPage === nextPage) {
        return prevPage
      }

      return nextPage
    })
  }, [step.width, onNavigationStateChange])

  const handleResize = () => {
    if (!listRef.current) {
      return
    }

    const visibleWidth = listRef.current.offsetWidth
    const contentWidth = listRef.current.scrollWidth

    setThumbWidth(Math.min((visibleWidth / contentWidth) * 100, 100))
  }

  const onScroll = () => {
    updateNavigationState()
  }

  const handleThumbMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDraggingRef.current || !thumbRef.current || !listRef.current) {
        return
      }

      const deltaX = e.clientX - startXRef.current
      const wrapper = listRef.current
      const scrollWidth = wrapper.scrollWidth - wrapper.offsetWidth

      const newScrollLeft = Math.min(
        Math.max(initialScrollLeftRef.current - deltaX * SPEED_MULTIPLIER, 0),
        scrollWidth
      )

      wrapper.scrollLeft = newScrollLeft
    },
    [listRef, SPEED_MULTIPLIER]
  )

  const handleThumbMouseUp = useCallback(() => {
    if (!thumbRef.current || !listRef.current) {
      return
    }

    isDraggingRef.current = false
    setIsDragging(false)
    document.removeEventListener('mousemove', handleThumbMouseMove)
    document.removeEventListener('mouseup', handleThumbMouseUp)
  }, [handleThumbMouseMove, listRef])

  const handleThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    if (!thumbRef.current || !listRef.current) {
      return
    }

    setIsDragging(true)
    isDraggingRef.current = true
    startXRef.current = e.clientX
    initialScrollLeftRef.current = listRef.current.scrollLeft

    document.addEventListener('mousemove', handleThumbMouseMove)
    document.addEventListener('mouseup', handleThumbMouseUp)
  }

  const moveTo = (index: number) => {
    if (!listRef?.current) {
      return
    }

    const maxScrollLeft = Math.max(
      listRef.current.scrollWidth - listRef.current.clientWidth,
      0
    )

    const nextScrollLeft = Math.min(
      Math.max(index * step.width, 0),
      maxScrollLeft
    )

    listRef.current.scrollLeft = nextScrollLeft
  }

  const onPrevClick = (): void => {
    if (!listRef?.current) {
      return
    }

    moveTo(currentPage - step.qtty)
  }

  const onNextClick = (): void => {
    if (!listRef?.current) {
      return
    }

    moveTo(currentPage + step.qtty)
  }

  useEffect(() => {
    handleResize()
    updateNavigationState()
  }, [itemQuantity, screenWidth, updateNavigationState])

  const maxScrollLeft = listRef.current
    ? Math.max(listRef.current.scrollWidth - listRef.current.clientWidth, 1)
    : 1

  const scrollProgress = listRef.current
    ? listRef.current.scrollLeft / maxScrollLeft
    : 0

  const maxThumbLeft = Math.max(100 - thumbWidth, 0)
  const thumbLeft = `${scrollProgress * maxThumbLeft}%`

  return (
    <>
      <div className="flex justify-between items-center px-4 md:px-0">
        <h2 className="flex text-restructure-primary desktop-heading-title3 tablet-heading-title3 mobile-heading-title3">
          {title}
        </h2>

        {!isMobile &&
          controlsStyle === 'top' &&
          (renderOnlyActiveControls
            ? navState.prev || navState.next
            : itemQuantity > step.qtty) && (
            <SliderControls
              {...{ onNextClick, onPrevClick, navState }}
              renderOnlyActiveControls={renderOnlyActiveControls}
              disableInactiveControls={disableInactiveControls}
            />
          )}
      </div>

      {itemQuantity > 0 ? (
        <div className="slider-container">
          <div className="slider-container relative">
            {controlsStyle === 'middle' &&
              !isMobile &&
              itemQuantity > 2 &&
              (renderOnlyActiveControls ? navState.prev : true) && (
                <div className="absolute left-2 top-0 h-full cursor-pointer z-10 flex justify-center items-center">
                  <PrevButton
                    isActive={navState.prev}
                    disableWhenInactive={disableInactiveControls}
                    onClick={onPrevClick}
                    style={controlsStyle}
                    clasName={`w-[40px] h-[40px] bg-white flex justify-center items-center rounded-round opacity-${
                      navState.prev ? 1 : 50
                    }`}
                  />
                </div>
              )}
            <ul
              ref={(element) => {
                listRef.current = element
                handleResize()
              }}
              className="slider-list px-4 md:px-0 "
              onScroll={onScroll}
            >
              {children}
            </ul>
            {controlsStyle === 'middle' &&
              !isMobile &&
              itemQuantity > 2 &&
              (renderOnlyActiveControls ? navState.next : true) && (
                <div className="absolute right-2 top-0 h-full cursor-pointer z-10 flex justify-center items-center">
                  <NextButton
                    isActive={navState.next}
                    disableWhenInactive={disableInactiveControls}
                    onClick={onNextClick}
                    style={controlsStyle}
                    clasName="w-[40px] h-[40px] bg-white flex justify-center items-center rounded-round"
                  />
                </div>
              )}
          </div>
          <div
            className={`slider-scrollbar ${hasScrollbar ? '' : 'hidden'} ${
              thumbWidth === 100 ? 'hidden' : ''
            } `}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <div className="slider-scrollbar-track" />
            <div
              onMouseDown={handleThumbMouseDown}
              style={{
                left: thumbLeft,
                width: `${thumbWidth}%`,
              }}
              ref={thumbRef}
              className="slider-scrollbar-thumb"
              tabIndex={0}
              role="slider"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={scrollProgress * 100}
              aria-label="scrollbar thumb"
            />
          </div>
        </div>
      ) : (
        <Skeleton className={skeletonStyles} />
      )}
    </>
  )
}

export default Slider
